import { useRouter } from '@base-app/web2'
import { memo, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { Application } from '../../../graphql/applications/types'
import { useAuth } from '../../../hooks/useAuth'
import { useSelectedApp } from '../../../hooks/useSelectedApp'
import { useViewerApp } from '../../../hooks/useViewerApp'
import { viewerOpensSection, viewerSeesResource } from '../../../lib/viewerPermissions'
import { NavLink } from '../../atoms'
import { AppSelector, UserInfo } from '../../molecules'

// /clients/<clientId>/apps/<appId>/<section>: the app a section page belongs to
const APP_SCOPED_PATH = /^\/clients\/([^/]+)\/apps\/([^/]+)\//

export function parseAppScope(path: string): { clientId: string; appId: string } | null {
  const match = path.match(APP_SCOPED_PATH)
  return match ? { clientId: match[1], appId: match[2] } : null
}

interface SidebarProps {
  userName: string
}

export const Sidebar = memo(({ userName }: SidebarProps) => {
  const { currentPath, navigate } = useRouter()
  const { t } = useTranslation('admin-mobifull')
  const { user } = useAuth()
  const { selectedClient, selectedApp, setSelectedApp, clearSelectedApp } = useSelectedApp()
  const initials = userName ? userName[0].toUpperCase() : '?'
  const isAdminUser =
    user?.features?.includes('superadmin') || user?.features?.includes('admin') || false

  const handleUserClick = useCallback(() => {
    navigate('/settings/me')
  }, [navigate])

  const handleNavigation = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      const to = e.currentTarget.getAttribute('href')
      if (to) {
        navigate(to)
      }
    },
    [navigate],
  )

  const links = useMemo(
    () => [
      { to: '/', label: t('nav.dashboard') },
      // Clients, Apps and Users are global-admin pages
      ...(isAdminUser
        ? [
            { to: '/clients', label: t('nav.clients') },
            { to: '/apps', label: t('nav.apps') },
            { to: '/administration/users', label: t('sidebar.users') },
          ]
        : []),
    ],
    [t, isAdminUser],
  )

  // The URL decides which app's sections show; the saved selection only fills in when the
  // page isn't inside an app. The app itself (name, accesses) is always read fresh, so a
  // bookmark to another app or an edit to this app's accesses shows the right links.
  const routeScope = useMemo(() => parseAppScope(currentPath), [currentPath])
  const activeAppId = routeScope?.appId ?? selectedApp?.id
  const { app: viewerApp } = useViewerApp(activeAppId)
  const activeApp = viewerApp?.id === activeAppId ? viewerApp : null

  // Keep the saved selection (and the picker) in step with the app being viewed
  useEffect(() => {
    if (!activeApp) {
      return
    }
    const savedAccesses = selectedApp?.accesses.join(',')
    if (
      selectedClient?.id !== activeApp.client_id ||
      selectedApp?.id !== activeApp.id ||
      savedAccesses !== activeApp.accesses.join(',')
    ) {
      setSelectedApp(
        {
          id: activeApp.client_id,
          name: selectedClient?.id === activeApp.client_id ? selectedClient.name : '',
        },
        { id: activeApp.id, name: activeApp.name, accesses: activeApp.accesses },
      )
    }
  }, [activeApp, selectedClient, selectedApp, setSelectedApp])

  // On a section page the URL wins, so applying another app moves to that app's same
  // section, or to the dashboard when the new app doesn't have it
  const handleApply = useCallback(
    (client: { id: string; name: string }, app: Application) => {
      setSelectedApp(client, { id: app.id, name: app.name, accesses: app.accesses })
      if (!routeScope) {
        return
      }
      const section = currentPath.split('/')[5] ?? ''
      // What the new app shows this user is the API's call (viewerPermissions), not ours
      navigate(
        viewerOpensSection(app, section) ? `/clients/${client.id}/apps/${app.id}/${section}` : '/',
      )
    },
    [currentPath, navigate, routeScope, setSelectedApp],
  )

  const scopedLinks = useMemo(() => {
    if (!activeApp) {
      return []
    }
    const base = `/clients/${activeApp.client_id}/apps/${activeApp.id}`
    // Each link names the resource whose visibility the API computed for this viewer
    const candidates = [
      { resource: 'collaborators', to: `${base}/collaborators`, label: t('sidebar.collaborators') },
      {
        resource: 'collaboratorfunctions',
        to: `${base}/collaborator-functions`,
        label: t('sidebar.collaboratorFunctions'),
      },
      { resource: 'departments', to: `${base}/departments`, label: t('sidebar.departments') },
      { resource: 'locations', to: `${base}/locations`, label: t('sidebar.locations') },
      { resource: 'radios', to: `${base}/radios`, label: t('sidebar.radios') },
      { resource: 'videoplaylists', to: `${base}/playlists`, label: t('sidebar.videoPlaylists') },
      { resource: 'videos', to: `${base}/videos`, label: t('sidebar.videos') },
      { resource: 'podcasts', to: `${base}/podcasts`, label: t('sidebar.podcasts') },
      { resource: 'banners', to: `${base}/banners`, label: t('sidebar.banners') },
      { resource: 'events', to: `${base}/events`, label: t('sidebar.events') },
      { resource: 'offerings', to: `${base}/offerings`, label: t('sidebar.offerings') },
      { resource: 'feed', to: `${base}/feed`, label: t('sidebar.feed') },
    ]
    const filtered = candidates
      .filter(c => viewerSeesResource(activeApp, c.resource))
      .map(c => ({ to: c.to, label: c.label, prefix: c.to }))

    if (activeApp.viewerCanManageUsers) {
      filtered.push({ to: `${base}/users`, label: t('sidebar.users'), prefix: `${base}/users` })
    }
    return filtered
  }, [activeApp, t])

  return (
    <aside className="sidebar">
      <UserInfo name={userName} initials={initials} onClick={handleUserClick} />
      <nav className="sidebar__nav">
        {links.map(link => (
          <NavLink
            key={link.to}
            href={link.to}
            label={link.label}
            active={currentPath === link.to}
            onClick={handleNavigation}
            className="sidebar__link"
          />
        ))}
      </nav>

      {user && (
        <AppSelector
          // Remount when the selection changes elsewhere (URL, Clear) so the picker shows it
          key={`${selectedClient?.id ?? ''}:${selectedApp?.id ?? ''}`}
          selectedClientId={selectedClient?.id}
          selectedAppId={selectedApp?.id}
          isAdmin={isAdminUser}
          onApply={handleApply}
          onClear={clearSelectedApp}
        />
      )}

      {scopedLinks.length > 0 && (
        <nav className="sidebar__scoped-nav">
          {scopedLinks.map(link => (
            <NavLink
              key={link.to}
              href={link.to}
              label={link.label}
              active={currentPath.startsWith(link.prefix)}
              onClick={handleNavigation}
              className="sidebar__link"
            />
          ))}
        </nav>
      )}
    </aside>
  )
})

Sidebar.displayName = 'Sidebar'
